// frontend/src/components/OnboardingTour.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import Joyride, { STATUS, ACTIONS, EVENTS } from 'react-joyride';

/**
 * OnboardingTour - 初回アクセス時にユーザーにUIツアーを表示するコンポーネント
 * 
 * @param {string} tourKey - LocalStorageに保存するユニークなキー (例: 'rural-choice-tour')
 * @param {Array} steps - Joyrideのステップ配列
 * @param {boolean} autoStart - trueに変更されるとツアーを開始（ガイドボタン用）
 * @param {boolean} isReady - ツアーを開始する準備ができているか（データロード完了など）
 * @param {function} onComplete - ツアー完了時のコールバック
 */
const OnboardingTour = ({
  tourKey,
  steps,
  autoStart = false,
  isReady = true,
  onComplete,
}) => {
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const hasCheckedInitial = useRef(false);

  // LocalStorageからツアー完了状態を確認
  const hasCompletedTour = useCallback(() => {
    try {
      return localStorage.getItem(`tour-completed-${tourKey}`) === 'true';
    } catch {
      return false;
    }
  }, [tourKey]);

  // ツアー完了状態を保存
  const markTourCompleted = useCallback(() => {
    try {
      localStorage.setItem(`tour-completed-${tourKey}`, 'true');
    } catch (e) {
      console.warn('Failed to save tour completion status:', e);
    }
  }, [tourKey]);

  // 初回アクセス時にツアーを自動開始
  useEffect(() => {
    if (isReady && !hasCheckedInitial.current) {
      hasCheckedInitial.current = true;
      if (!hasCompletedTour()) {
        const timer = setTimeout(() => {
          setStepIndex(0);
          setRun(true);
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [isReady, hasCompletedTour]);

  // ガイドボタンからの手動開始
  useEffect(() => {
    if (autoStart && isReady) {
      const timer = setTimeout(() => {
        setStepIndex(0);
        setRun(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [autoStart, isReady]);

  // Joyrideコールバック
  const handleJoyrideCallback = useCallback((data) => {
    const { status, action, index, type } = data;

    // ステップ変更後の処理
    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    }

    // ツアー終了時の処理
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRun(false);
      markTourCompleted();
      if (onComplete) {
        onComplete(status === STATUS.FINISHED);
      }
    }
  }, [markTourCompleted, onComplete]);

  // ツアーを手動で開始するための関数（外部から呼び出し可能）
  const startTour = useCallback(() => {
    setStepIndex(0);
    setRun(true);
  }, []);

  // ツアーをリセットする関数（開発・デバッグ用）
  const resetTour = useCallback(() => {
    try {
      localStorage.removeItem(`tour-completed-${tourKey}`);
    } catch {
      // ignore
    }
  }, [tourKey]);

  // 親コンポーネントからアクセスできるように公開
  React.useEffect(() => {
    if (window) {
      window[`${tourKey}Tour`] = { startTour, resetTour };
    }
    return () => {
      if (window && window[`${tourKey}Tour`]) {
        delete window[`${tourKey}Tour`];
      }
    };
  }, [tourKey, startTour, resetTour]);

  if (!steps || steps.length === 0) {
    return null;
  }

  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous
      showProgress
      showSkipButton
      scrollToFirstStep={true}
      disableScrolling={false}
      scrollOffset={100}
      spotlightClicks={false}
      callback={handleJoyrideCallback}
      styles={{
        options: {
          primaryColor: '#1e3a5f',
          zIndex: 10000,
          arrowColor: '#fff',
          backgroundColor: '#fff',
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          textColor: '#333',
          width: 380,
        },
        buttonNext: {
          backgroundColor: '#1e3a5f',
          borderRadius: '8px',
          color: '#fff',
          padding: '10px 20px',
          fontSize: '14px',
          fontWeight: '600',
        },
        buttonBack: {
          color: '#1e3a5f',
          marginRight: 10,
          fontSize: '14px',
        },
        buttonSkip: {
          color: '#888',
          fontSize: '13px',
        },
        buttonClose: {
          display: 'none',
        },
        tooltip: {
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        tooltipTitle: {
          fontSize: '18px',
          fontWeight: '700',
          marginBottom: '12px',
          color: '#1e3a5f',
        },
        tooltipContent: {
          fontSize: '14px',
          lineHeight: '1.6',
          color: '#555',
        },
        spotlight: {
          borderRadius: '8px',
        },
        beacon: {
          display: 'none',
        },
      }}
      locale={{
        back: '戻る',
        close: '閉じる',
        last: '完了',
        next: '次へ',
        skip: 'スキップ',
      }}
      floaterProps={{
        disableAnimation: false,
      }}
    />
  );
};

export default OnboardingTour;
